/**
 * App Component
 * Main application component with routing and providers
 */

import { useEffect } from 'react';
import { hasAdminAccess } from './lib/roles';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './stores/auth.store';
import { useThemeStore } from './stores/theme.store';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { GuestRoute } from './components/auth/GuestRoute';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { DashboardPage } from './pages/DashboardPage';
import { MembersPage } from './pages/MembersPage';
import { MemberDetailsPage } from './pages/MemberDetailsPage';
import { TeamsPage } from './pages/TeamsPage';
import { TeamDetailPage } from './pages/TeamDetailPage';
import { DepartmentsPage } from './pages/DepartmentsPage';
import { DepartmentDetailPage } from './pages/DepartmentDetailPage';
import { GroupsPage } from './pages/GroupsPage';
import { GroupDetailPage } from './pages/GroupDetailPage';
import { UsersPage } from './pages/UsersPage';
import { QuizPage } from './pages/QuizPage';
import { CreateAccountPage } from './pages/CreateAccountPage';
import { RequestNewInvitePage } from './pages/RequestNewInvitePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { ImportDataPage } from './pages/ImportDataPage';
import { ToastContainer } from './components/ui/ToastContainer';
import { ScrollToTop } from './components/ui/ScrollToTop';
import { authService } from './services/auth.service';
import { isTokenExpired } from './lib/tokenUtils';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * RoleBasedRedirect Component
 * Redirects users to their appropriate landing page based on role
 * - Admins → /dashboard
 * - Regular users → /home
 */
const RoleBasedRedirect = () => {
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on user role
  if (hasAdminAccess(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/home" replace />;
};

function App() {
  const initAuth = useAuthStore((state) => state.initAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setToken = useAuthStore((state) => state.setToken);
  const initTheme = useThemeStore((state) => state.init);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Initialize theme state (sync with DOM class set by FOUC script)
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Cross-tab session synchronization
  // Listen for localStorage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== 'tw_access_token' && e.key !== 'tw_user_data') return;

      // Logout in another tab (either key removed) → clear immediately.
      if (!e.newValue) {
        clearAuth();
        return;
      }

      // Cross-tab login writes tw_access_token THEN tw_user_data as two separate
      // storage events. Reacting to the token-only interim would make initAuth()
      // see an orphan (no user_data yet) and scrub the just-written token
      // (TW-168 + TW-277). Only (re)initialize once BOTH keys are committed —
      // this also covers token refresh (user_data already present) and is
      // independent of write order.
      if (
        localStorage.getItem('tw_access_token') &&
        localStorage.getItem('tw_user_data')
      ) {
        initAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [clearAuth, initAuth]);

  // Re-sync auth state when tab becomes visible (handles cross-tab login/logout)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        initAuth();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [initAuth]);

  // Proactive token refresh - check every 5 minutes and refresh if token will expire soon
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('tw_access_token');
      if (!token) return;

      // Check if token will expire in the next 5 minutes (300 seconds)
      if (isTokenExpired(token, 300)) {
        console.log('🔐 [Auto-Refresh] Token expiring soon, refreshing proactively...');

        try {
          const response = await authService.refresh();
          if (response.token) {
            localStorage.setItem('tw_access_token', response.token);
            setToken(response.token);
            console.log('🔐 [Auto-Refresh] ✅ Token refreshed successfully');
          }
        } catch (error) {
          console.error('🔐 [Auto-Refresh] ❌ Failed to refresh token:', error);
        }
      }
    };

    // Check immediately on mount (if authenticated)
    checkAndRefreshToken();

    // Then check every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, setToken]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <ToastContainer />
        <Routes>
          {/* Public routes — redirect to app if already authenticated */}
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/request-new-invite" element={<RequestNewInvitePage />} />
          </Route>

          {/* Token-based routes — accessible whether or not the user is signed in.
              Authenticated users see an in-page warning to sign out first. */}
          <Route path="/create-account" element={<CreateAccountPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected routes - Regular users */}
          <Route element={<ProtectedRoute />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/members/:id" element={<MemberDetailsPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:id" element={<TeamDetailPage />} />
            <Route path="/departments" element={<DepartmentsPage />} />
            <Route path="/departments/:id" element={<DepartmentDetailPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
          </Route>

          {/* Admin-only routes */}
          <Route element={<ProtectedRoute requireAdmin={true} />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/import" element={<ImportDataPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/groups" element={<GroupsPage />} />
            <Route path="/groups/:id" element={<GroupDetailPage />} />
            <Route path="/audit-logs" element={<AuditLogsPage />} />
          </Route>

          {/* Role-based redirect */}
          <Route path="/" element={<RoleBasedRedirect />} />
          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
